import CommentIcon from "@mui/icons-material/Comment";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import { CSSObject, keyframes, Theme } from "@mui/material";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Comments from "work/proposals/comments/_view/comments";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import Proposal from "work/proposals/proposal";
import ProposalField, { HumanReadableProposalFieldName } from "work/proposals/proposal-fields/proposal-field";

const pulse = keyframes`
  0% {
    transform: scale(0.9);
    opacity: 0.85;
  }
  50% {
    transform: scale(1.2);
    opacity: 1.0;
  }
  100% {
    transform: scale(0.9);
    opacity: 0.85;
  }
`;
const openedMixin = (): CSSObject => ({
  overflowX: "hidden",
});
const closedMixin = (theme: Theme): CSSObject => ({
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});
const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(({ theme }) => ({
  boxSizing: "border-box",
  flexShrink: 0,
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(),
        "& .MuiDrawer-paper": openedMixin(),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
  whiteSpace: "nowrap",
}));
const CommentsPanel = styled(Drawer, { shouldForwardProp: (prop) => prop !== "closeAfterTransition" })(({ theme }) => ({
  '& .MuiPaper-root': {
    backdropFilter: 'blur(15px) brightness(175%)',
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
  },
  position: "fixed",
  zIndex: theme.zIndex.modal + 1,
}));
const CommentsPanelContainer = styled("div")(() => ({
  flex: 1,
  height: "100%",
}));
const CommentsPanelContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  padding: theme.spacing(2),
  width: "400px",
}));
const TitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "row",
  paddingBottom: theme.spacing(1),
}));
const CommentsPanelButtonContainer = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(0),
}));
const CommentsPanelButton = styled(Button)(() => ({
  transform: "translateX(30px) rotate(90deg)",
  transformOrigin: "left center",
}));
const CommentIconContainer = styled("div", { shouldForwardProp: (prop) => prop !== "active" })<{ active?: boolean }>(
  ({ active }) => ({
    paddingBottom: 0,
    position: "relative",
    rotate: "-90deg",
    zIndex: 0,
    ...(active && {
      animation: `${pulse} 1.5s ease-in-out infinite`,
    }),
  })
);

type CommentsDrawerProps = {
  proposal: Proposal | null | undefined;
  commentThreads: CommentThread[] | undefined;
  currentCommentField: ProposalField | undefined;
  activeReviewField: ProposalField | undefined;
  isOpen: boolean;
  isSaving: boolean;
  title?: string;
  onOpen: () => void;
  onClose: () => void;
};

export default function CommentsDrawer(props: Readonly<CommentsDrawerProps>) {
  const { proposal, commentThreads, currentCommentField, activeReviewField, isOpen, isSaving, title, onOpen, onClose } =
    props;

  const currentFieldUnreadCount =
    commentThreads
      ?.filter((thread) => thread.field.isEqualTo(currentCommentField ?? ProposalField.General))
      .reduce((acc, thread) => acc + (thread.numUnreadMessages ?? 0), 0) ?? 0;

  function getTitle(): string {
    if (title) return `${title} Comments`;
    return `${HumanReadableProposalFieldName[currentCommentField?.name ?? ProposalField.General.name]} Comments`;
  }

  function renderComments() {
    if (proposal?.id) {
      return (
        <Comments
          proposal={proposal}
          field={currentCommentField ?? ProposalField.General}
          commentThreads={commentThreads ?? []}
          isSaving={isSaving}
        />
      );
    }
  }

  return (
    <CommentsPanel open={isOpen} anchor="right" variant="permanent" closeAfterTransition={false}>
      {!isOpen && (
        <CommentsPanelButtonContainer>
          <CommentsPanelButton
            variant="text"
            size="large"
            startIcon={
              <CommentIconContainer
                active={
                  activeReviewField?.isEqualTo(currentCommentField ?? ProposalField.General) &&
                  currentFieldUnreadCount > 0
                }
              >
                <Badge
                  color="secondary"
                  overlap="circular"
                  variant={currentFieldUnreadCount ? "standard" : "dot"}
                  badgeContent={currentFieldUnreadCount > 0 ? currentFieldUnreadCount : undefined}
                  invisible={
                    !commentThreads?.some((thread) =>
                      thread.field.isEqualTo(currentCommentField ?? ProposalField.General)
                    )
                  }
                >
                  <CommentIcon />
                </Badge>
              </CommentIconContainer>
            }
            onClick={onOpen}
          >
            {getTitle()}
          </CommentsPanelButton>
        </CommentsPanelButtonContainer>
      )}
      {isOpen && (
        <CommentsPanelContainer>
          <CommentsPanelContent>
            <TitleBar>
              <Typography variant="h5">{getTitle()}</Typography>
              <Tooltip title="Minimize comments panel">
                <IconButton size="medium" onClick={onClose}>
                  <KeyboardTabIcon fontSize="large" />
                </IconButton>
              </Tooltip>
            </TitleBar>
            {renderComments()}
          </CommentsPanelContent>
        </CommentsPanelContainer>
      )}
    </CommentsPanel>
  );
}
